/**
 * Customer Order History - Enterprise Support Feature
 * 
 * Features:
 * - Customer search by name/phone
 * - Complete order history view
 * - Order detail modal with all items
 * - One-click reorder functionality
 * - Incident/Credit creation for refunds
 * 
 * Design: Modern, Space Grotesk typography, Teal accents
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Search, Phone, MapPin, ShoppingBag, Calendar, RefreshCw, User, 
  Euro, Package, Truck, Clock, AlertCircle, CheckCircle, XCircle,
  FileText, RotateCcw, DollarSign, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

export default function CustomerOrderHistory() {
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Modals
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  const [showIncidentModal, setShowIncidentModal] = useState(false);
  
  // Incident/Credit form
  const [incidentReason, setIncidentReason] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [incidentNotes, setIncidentNotes] = useState('');

  // Search customers
  const searchCustomers = async () => {
    if (searchQuery.length < 2) {
      toast.error('Suchbegriff zu kurz', { description: 'Mindestens 2 Zeichen eingeben' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/customers/search?query=${encodeURIComponent(searchQuery)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Search failed');
      }

      const data = await response.json();
      setCustomers(data.customers || []);
      
      if (data.customers?.length === 0) {
        toast.info('Keine Kunden gefunden', { description: 'Bitte anderen Suchbegriff versuchen' });
      }
    } catch (error) {
      toast.error('Fehler bei Kundensuche', { description: error.message });
    } finally {
      setLoading(false);
    }
  };

  // Load customer orders
  const loadCustomerOrders = async (customer) => {
    setSelectedCustomer(customer);
    setLoading(true);

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/customers/${customer.customer_id}/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load orders');

      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      toast.error('Fehler beim Laden der Bestellungen', { description: error.message });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Reorder
  const handleReorder = async (order) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/orders/${order.order_id}/reorder`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Reorder failed');

      const data = await response.json();
      toast.success('Bestellung dupliziert!', { 
        description: `Neue Bestellung #${data.order_number} erstellt` 
      });
      
      // Refresh orders
      loadCustomerOrders(selectedCustomer);
    } catch (error) {
      toast.error('Fehler beim Wiederholen', { description: error.message });
    }
  };

  // Create incident/credit
  const handleCreateIncident = async () => {
    if (!incidentReason || !creditAmount) {
      toast.error('Pflichtfelder fehlen', { description: 'Grund und Betrag sind erforderlich' });
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_BASE}/api/orders/${selectedOrder.order_id}/incidents`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          reason: incidentReason,
          credit_amount: parseFloat(creditAmount),
          notes: incidentNotes
        })
      });

      if (!response.ok) throw new Error('Failed to create incident');

      const data = await response.json();
      toast.success('Vorfall erstellt!', { 
        description: `Gutschrift über ${creditAmount}€ ausgestellt` 
      });
      
      // Close modal and refresh
      setShowIncidentModal(false);
      setIncidentReason('');
      setCreditAmount('');
      setIncidentNotes('');
      loadCustomerOrders(selectedCustomer);
    } catch (error) {
      toast.error('Fehler beim Erstellen', { description: error.message });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0B1220] via-[#0E1628] to-[#0B1220] p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Kundenhistorie
            </h1>
            <p className="text-slate-400">
              Suchen Sie Kunden und sehen Sie deren komplette Bestellhistorie
            </p>
          </div>
        </div>

        {/* Search Bar - MODERNIZED */}
        <Card className="bg-gradient-to-br from-[#111C32] to-[#0E1628] border-slate-700/50 shadow-xl">
          <CardContent className="p-6">
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchCustomers()}
                  placeholder="Kundenname oder Telefonnummer suchen..."
                  className="pl-12 h-14 bg-[#0E1628] border-slate-700/60 text-white text-lg
                           focus:border-teal-500/60 focus:ring-2 focus:ring-teal-500/20"
                  style={{ fontFamily: "'Inter', sans-serif" }}
                />
              </div>
              <Button
                onClick={searchCustomers}
                disabled={loading}
                className="bg-gradient-to-r from-teal-500 to-emerald-500 hover:from-teal-400 hover:to-emerald-400 
                         text-white h-14 px-8 font-semibold shadow-lg shadow-teal-500/20
                         hover:shadow-xl hover:shadow-teal-500/30 active:scale-[0.98] transition-all"
              >
                {loading ? <RefreshCw className="animate-spin" size={20} /> : <Search size={20} />}
                <span className="ml-2">Suchen</span>
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Customer List */}
          <Card className="lg:col-span-1 bg-[#111C32]/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <User className="w-5 h-5 text-teal-400" />
                Kunden ({customers.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[600px]">
                <div className="space-y-2">
                  {customers.map((customer) => (
                    <button
                      key={customer.customer_id}
                      onClick={() => loadCustomerOrders(customer)}
                      className={`
                        w-full p-4 rounded-lg border-2 text-left transition-all duration-200
                        ${selectedCustomer?.customer_id === customer.customer_id
                          ? 'border-teal-500 bg-teal-500/15 shadow-lg'
                          : 'border-slate-700/50 bg-[#1A2642]/40 hover:border-teal-400/40 hover:bg-[#1E2D4A]'
                        }
                      `}
                    >
                      <div className="font-semibold text-white mb-1">{customer.name}</div>
                      <div className="text-sm text-slate-400 flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {customer.phone}
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                        <span>{customer.total_orders || 0} Bestellungen</span>
                        <span>•</span>
                        <span>{(customer.total_spent || 0).toFixed(2)}€ Umsatz</span>
                      </div>
                    </button>
                  ))}
                  
                  {customers.length === 0 && !loading && (
                    <div className="text-center py-12 text-slate-500">
                      <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Keine Kunden gefunden</p>
                      <p className="text-xs mt-1">Starten Sie eine Suche</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Order History */}
          <Card className="lg:col-span-2 bg-[#111C32]/80 border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                <ShoppingBag className="w-5 h-5 text-teal-400" />
                {selectedCustomer ? `Bestellhistorie - ${selectedCustomer.name}` : 'Bestellhistorie'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedCustomer ? (
                <ScrollArea className="h-[600px]">
                  <div className="space-y-3">
                    {orders.map((order) => (
                      <div
                        key={order.order_id}
                        className="p-5 rounded-xl border-2 border-slate-700/50 bg-[#1A2642]/40 
                                 hover:border-teal-400/40 hover:bg-[#1E2D4A] transition-all duration-200
                                 hover:-translate-y-0.5 hover:shadow-md"
                      >
                        {/* Order Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="font-semibold text-white text-lg mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                              Bestellung #{order.order_number}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-slate-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5" />
                                {new Date(order.created_at).toLocaleDateString('de-DE')}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-3.5 h-3.5" />
                                {new Date(order.created_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-2xl font-bold text-teal-400" style={{ fontFamily: "'Fira Code', monospace" }}>
                              {order.total_amount?.toFixed(2)}€
                            </div>
                            <Badge variant="outline" className={`
                              mt-1
                              ${order.status === 'completed' ? 'text-emerald-400 border-emerald-400/50 bg-emerald-400/10' : ''}
                              ${order.status === 'cancelled' ? 'text-red-400 border-red-400/50 bg-red-400/10' : ''}
                            `}>
                              {order.status === 'completed' ? 'Abgeschlossen' : order.status}
                            </Badge>
                          </div>
                        </div>

                        {/* Order Items Preview */}
                        <div className="space-y-1.5 mb-4">
                          {order.items?.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="text-sm text-slate-300 flex items-center gap-2">
                              <div className="w-1.5 h-1.5 rounded-full bg-teal-500/60" />
                              <span className="font-medium">{item.quantity}x</span>
                              <span>{item.name}</span>
                            </div>
                          ))}
                          {order.items?.length > 3 && (
                            <div className="text-xs text-slate-500 pl-3.5">
                              + {order.items.length - 3} weitere Artikel
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowOrderDetail(true);
                            }}
                            className="flex-1 border-slate-600 text-white hover:bg-slate-700/60 hover:border-teal-400/40"
                          >
                            <FileText className="w-4 h-4 mr-1.5" />
                            Details
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReorder(order)}
                            className="flex-1 border-slate-600 text-emerald-400 hover:bg-emerald-500/10 hover:border-emerald-400/50"
                          >
                            <RotateCcw className="w-4 h-4 mr-1.5" />
                            Wiederholen
                          </Button>
                          
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowIncidentModal(true);
                            }}
                            className="flex-1 border-slate-600 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400/50"
                          >
                            <DollarSign className="w-4 h-4 mr-1.5" />
                            Vorfall
                          </Button>
                        </div>
                      </div>
                    ))}
                    
                    {orders.length === 0 && !loading && (
                      <div className="text-center py-16 text-slate-500">
                        <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-20" />
                        <p>Keine Bestellungen gefunden</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              ) : (
                <div className="h-[600px] flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <User className="w-16 h-16 mx-auto mb-4 opacity-20" />
                    <p className="text-sm">Wählen Sie einen Kunden aus</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Order Detail Modal */}
      <Dialog open={showOrderDetail} onOpenChange={setShowOrderDetail}>
        <DialogContent className="sm:max-w-[700px] bg-[#0E1628] border-slate-700/50 shadow-2xl">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-teal-500 to-emerald-500" />
          
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Bestellung #{selectedOrder?.order_number}
            </DialogTitle>
          </DialogHeader>

          {selectedOrder && (
            <div className="space-y-4">
              {/* Order Meta */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-[#111C32]/50 rounded-lg border border-slate-700/50">
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Datum</div>
                  <div className="text-white font-medium">
                    {new Date(selectedOrder.created_at).toLocaleString('de-DE')}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Zahlungsart</div>
                  <div className="text-white font-medium capitalize">
                    {selectedOrder.payment_method || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div>
                <div className="text-sm font-semibold text-slate-300 mb-3 uppercase tracking-wide">
                  Bestellte Artikel
                </div>
                <div className="space-y-2">
                  {selectedOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-[#1A2642]/40 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-teal-500/20 flex items-center justify-center text-teal-400 font-bold text-sm">
                          {item.quantity}
                        </div>
                        <div>
                          <div className="text-white font-medium">{item.name}</div>
                          {item.unit_price && (
                            <div className="text-xs text-slate-400">{item.unit_price.toFixed(2)}€ pro Stück</div>
                          )}
                        </div>
                      </div>
                      <div className="text-teal-400 font-bold" style={{ fontFamily: "'Fira Code', monospace" }}>
                        {item.subtotal?.toFixed(2)}€
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Total */}
              <Separator className="bg-slate-700/50" />
              <div className="flex items-center justify-between p-4 bg-teal-500/10 rounded-lg border border-teal-500/30">
                <span className="text-white font-semibold text-lg" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  Gesamtbetrag
                </span>
                <span className="text-3xl font-bold text-teal-400" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                  {selectedOrder.total_amount?.toFixed(2)}€
                </span>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowOrderDetail(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Incident/Credit Modal */}
      <Dialog open={showIncidentModal} onOpenChange={setShowIncidentModal}>
        <DialogContent className="sm:max-w-[600px] bg-[#0E1628] border-slate-700/50 shadow-2xl">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-500 to-orange-500" />
          
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Vorfall / Gutschrift erstellen
            </DialogTitle>
            <p className="text-sm text-slate-400 mt-2">
              Bestellung #{selectedOrder?.order_number}
            </p>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label className="text-slate-300 mb-2 block">Grund *</Label>
              <Input
                value={incidentReason}
                onChange={(e) => setIncidentReason(e.target.value)}
                placeholder="z.B. Falsche Lieferung, Kaltes Essen, etc."
                className="bg-[#111C32] border-slate-700 text-white"
              />
            </div>

            <div>
              <Label className="text-slate-300 mb-2 block">Gutschrift Betrag (€) *</Label>
              <Input
                type="number"
                step="0.01"
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="0.00"
                className="bg-[#111C32] border-slate-700 text-white"
                style={{ fontFamily: "'Fira Code', monospace" }}
              />
            </div>

            <div>
              <Label className="text-slate-300 mb-2 block">Notizen (optional)</Label>
              <Textarea
                value={incidentNotes}
                onChange={(e) => setIncidentNotes(e.target.value)}
                placeholder="Weitere Details zum Vorfall..."
                className="bg-[#111C32] border-slate-700 text-white min-h-[100px]"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowIncidentModal(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleCreateIncident}
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400
                       text-white shadow-lg shadow-amber-500/20"
            >
              <DollarSign className="w-4 h-4 mr-2" />
              Gutschrift erstellen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
